import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EXPORT_FORMATS, type ExportFormat } from "@sga/shared";
import { apiDelete, apiGet, apiPatch, apiPost } from "../lib/api.js";

/**
 * The scheduled-exports manager — docs/01-PRODUCT-SPEC.md §9.3.
 *
 * The cron is entered as a cron, with presets for the two schedules anyone actually asks for.
 * Hiding it behind a builder would mean inventing a second schedule language, and a coordinator who
 * needs "07:00 on the first Monday of the month" is better served by the real thing plus a
 * description of what it will do.
 *
 * A schedule must name a form. The worker skips any row without one (`scheduledExports.ts`: "has
 * no form; skipping"), so a form is not a refinement here — it is the difference between a schedule
 * that runs and a row that sits in the table doing nothing. Mounted on the Exports page with no
 * `formId` prop, this component had no way to supply one and every submission was rejected.
 */

interface FormSummary {
  id: string;
  title: string;
  status: string;
  programmeName?: string;
}

interface ScheduledExport {
  id: string;
  formId: string | null;
  name: string;
  cron: string;
  timezone: string;
  format: string;
  recipients: string[];
  destination: string;
  isActive: boolean;
  lastRunAt: string | null;
}

const PRESETS: { label: string; cron: string }[] = [
  { label: "Every weekday at 07:00", cron: "0 7 * * 1-5" },
  { label: "Every Monday at 07:00", cron: "0 7 * * 1" },
  { label: "Daily at 18:00", cron: "0 18 * * *" },
  { label: "Hourly", cron: "0 * * * *" },
];

export const ScheduledExports = ({ formId }: { formId?: string }): JSX.Element => {
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [cron, setCron] = useState("0 7 * * 1");
  const [format, setFormat] = useState<ExportFormat>("xlsx");
  const [recipients, setRecipients] = useState("");
  const [chosenFormId, setChosenFormId] = useState("");

  // Only when this is the standalone Exports page. Embedded under a form, the form is already known
  // and offering a picker would invite scheduling an export of a form the user is not looking at.
  const forms = useQuery({
    queryKey: ["forms"],
    queryFn: () => apiGet<{ data: FormSummary[] }>("/forms"),
    enabled: !formId,
  });

  const targetFormId = formId ?? chosenFormId;

  const schedules = useQuery({
    queryKey: ["scheduled-exports"],
    queryFn: () => apiGet<{ data: ScheduledExport[] }>("/scheduled-exports"),
  });

  const invalidate = () => client.invalidateQueries({ queryKey: ["scheduled-exports"] });

  const create = useMutation({
    mutationFn: () =>
      apiPost("/scheduled-exports", {
        formId: targetFormId,
        name,
        cron,
        timezone: "Africa/Lagos",
        format,
        recipients: recipients
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean),
        destination: "email",
        isActive: true,
      }),
    onSuccess: () => {
      setName("");
      setRecipients("");
      void invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiPatch(`/scheduled-exports/${id}`, { isActive }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/scheduled-exports/${id}`),
    onSuccess: invalidate,
  });

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">Scheduled exports</h2>

      <form
        className="flex flex-col gap-2 rounded-lg border p-3 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() && targetFormId) create.mutate();
        }}
      >
        {!formId && (
          <select
            aria-label="Form to export"
            value={chosenFormId}
            onChange={(event) => setChosenFormId(event.target.value)}
            className="w-full rounded-md border px-2 py-1"
          >
            <option value="">Choose the form to export…</option>
            {(forms.data?.data ?? []).map((form) => (
              <option key={form.id} value={form.id}>
                {form.title}
              </option>
            ))}
          </select>
        )}

        <div className="flex flex-wrap gap-2">
          <input
            aria-label="Schedule name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Weekly Delta team export"
            className="w-64 rounded-md border px-2 py-1"
          />
          <select
            aria-label="Schedule"
            value={cron}
            onChange={(event) => setCron(event.target.value)}
            className="rounded-md border px-2 py-1"
          >
            {PRESETS.map((preset) => (
              <option key={preset.cron} value={preset.cron}>
                {preset.label}
              </option>
            ))}
          </select>
          <input
            aria-label="Cron expression"
            value={cron}
            onChange={(event) => setCron(event.target.value)}
            className="w-36 rounded-md border px-2 py-1 font-mono"
          />
          <select
            aria-label="Format"
            value={format}
            onChange={(event) => setFormat(event.target.value as ExportFormat)}
            className="rounded-md border px-2 py-1"
          >
            {EXPORT_FORMATS.map((value) => (
              <option key={value} value={value}>
                {value.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <input
          aria-label="Recipients, comma separated"
          value={recipients}
          onChange={(event) => setRecipients(event.target.value)}
          placeholder="delta.team@seplatgrowthacademy.com, coordinator@…"
          className="rounded-md border px-2 py-1"
        />

        <p className="text-xs text-muted-foreground">
          Runs in Africa/Lagos. A scheduled export always redacts personal data — nobody is present
          to hold the permission when it runs.
        </p>

        <div className="flex flex-col gap-2">
          <div>
            <button
              type="submit"
              disabled={create.isPending || !targetFormId || !name.trim()}
              className="rounded-md bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-60"
            >
              Add schedule
            </button>
          </div>

          {/* A rejected POST used to leave the button looking untouched: the mutation's error was
              never rendered, so "nothing happened" was the entire feedback for a 400. */}
          {create.isError && (
            <p role="alert" className="text-xs text-brand-red">
              {create.error instanceof Error
                ? create.error.message
                : "That schedule could not be saved."}
            </p>
          )}
          {create.isSuccess && !create.isPending && (
            <p role="status" className="text-xs text-muted-foreground">
              Schedule added.
            </p>
          )}
        </div>
      </form>

      <ul className="flex flex-col gap-2">
        {(schedules.data?.data ?? []).map((schedule) => (
          <li
            key={schedule.id}
            className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm"
          >
            <span>
              <span className="block font-medium">{schedule.name}</span>
              <code className="text-xs text-muted-foreground">
                {schedule.cron} · {schedule.timezone} · {schedule.format.toUpperCase()} ·{" "}
                {schedule.recipients.length} recipient
                {schedule.recipients.length === 1 ? "" : "s"}
              </code>
              {schedule.lastRunAt && (
                <span className="block text-xs text-muted-foreground">
                  Last run{" "}
                  {new Date(schedule.lastRunAt).toLocaleString("en-NG", {
                    timeZone: "Africa/Lagos",
                  })}
                </span>
              )}
            </span>
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggle.mutate({ id: schedule.id, isActive: !schedule.isActive })}
                className="rounded-md border px-2 py-1"
              >
                {schedule.isActive ? "Pause" : "Resume"}
              </button>
              <button
                type="button"
                onClick={() => remove.mutate(schedule.id)}
                className="rounded-md border px-2 py-1 text-brand-red"
              >
                Delete
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
